import { TabHeader } from "components/TabHeader";
import { useEffect, useMemo, useRef, useState } from "react";
import { Flex, useBreakpointValue, useDisclosure } from "@chakra-ui/react";
import { UtilityButton } from "components/RFQ/UtilityButton";
import { InviteSupplierButton } from "components/Pages/RFQDetail/InviteSupplierButton";
import { HeaderCard } from "components/Pages/RFQDetail/HeaderCard";
import { RFQDetailButtonTab } from "components/Pages/RFQDetail/RFQDetailButtonTab";
import RFQListSubmission from "./RFQListSubmission/RFQListSubmission";
import { InviteSupplierDrawer } from "components/Pages/RFQDetail/InviteSupplierDrawer";
import BackButton from "newui/components/BackButton";
import { navigate, useLocation } from "@reach/router";
import { InvitationSentModal } from "newui/components/ui/InvitationSentModal";
import { NotFound } from "newui/features/ErrorPages/NotFound";
import { Button } from "newui/components/Button";
import {
	isRFQEditable,
	isRFQReadyForAward,
	isRFQSubmissionDateExtendable,
	isRFQAwarded,
} from "utils/helpers";
import { useDispatch } from "react-redux";
import apiV2 from "api-v2";
import { removeAllToListDirect } from "actions/searchActions";
import ExtendSubmissionDeadlineModal from "./RFQUpdateSubmissionDeadline";
import { ClientAddendumContainer } from "../CreateRFQ/CreateRFQPage/addendum/ClientAddendumContainer";
import { AwardDeclineSupplierDrawer } from "components/Pages/RFQDetail/AwardDeclineSupplierDrawers/AwardDeclineSupplierDrawer";
import { hasSubmissionDeadlinePassed } from "components/RFQ/SubmissionDeadline/SubmissionDeadline";
import { RFX_TABS } from "utils/constants";
import PdfDownloadButton from "components/RFQ/PdfDownloadButton";
import { useCurrentUserHasFeatureFlag } from "hooks/featureFlags";
import { rfxSubmission } from "newui/features/Rfx/Submission";
import { FeatureFlagType } from "newui/features/Auth/types/FeatureFlag.type";
import { useRfxData } from "newui/features/Rfx/data-provider/RfxDataProvider";
import { LoadingSpinner } from "components/Loading/LoadingSpinner";
import CommunicationsPanel from "newui/features/Conversation";
import { fetchConversationsForClientRfx } from "newui/features/Conversation/api";
import { useAuthContext } from "newui/features/Auth/context/AuthContextProvider";
import { ClarificationsRegister } from "newui/features/Rfx/Clarifications";
import { EvaluationTabDisplay } from "newui/features/Rfx/Evaluation";
import { ReadOnlyGuard } from "newui/features/ExecutiveView";
import { getRfxListTabForStatus } from "newui/features/Rfx/tabs/rfx-status-tab";

/**
 * This is the view that the Procurer sees of an RFx - the overall view AFTER submission
 * @param {*} param0 
 * @returns 
 */
const ProcurerRFx = ({ currentTab, contextId }) => {
	const {
		hasRfxLoaded,
		rfxId,
		rfxData,
		rfxTabTotals,
		reloadRfxData,
		setRfxTabTotals,
		updateLocalRfxData,
	} = useRfxData();
	const dispatch = useDispatch();
	const [guestName, setGuestName] = useState();
	const [guestEmail, setGuestEmail] = useState();
	const addendumRef = useRef(null);
	const supplierRef = useRef(null);

	const { clientPaths } = useAuthContext();

	const [addendumData, setAddendumData] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [checkedRow, setCheckedRow] = useState([]);
	const [isNotFound, setIsNotFound] = useState(false);
	const isSubmissionDateExtendable = isRFQSubmissionDateExtendable(
		rfxData?.status
	);
	const RfxIsAwarded = isRFQAwarded(rfxData?.status);
	const [isExtendDateModelOpen, setIsExtendDateModelOpen] = useState(false);
	const [invitationSentEmails, setInvitationSentEmails] = useState(null);
	const location = useLocation();

	// The send-RFx flow navigates here and hands over its recipients in router
	// state; consume it once so a refresh or a later visit doesn't replay the modal.
	useEffect(() => {
		const carriedEmails = location?.state?.invitationSentEmails;

		if (!carriedEmails?.length) {
			return;
		}

		setInvitationSentEmails(carriedEmails);
		navigate(location.pathname, { replace: true, state: {} });
	}, [location]);

	const rfqContentRef = useRef(null);

	const hasDeadlinePassed =
		rfxData &&
		hasSubmissionDeadlinePassed(
			rfxData.submissionDeadline,
			rfxData.submissionTimezone
		);

	const isResponseRestricted = useCurrentUserHasFeatureFlag(
		FeatureFlagType.RESTRICT_RFX_SUBMISSION_VIEW
	);

	const [{ conversations, initialConversation }, setConversations] = useState({
		conversations: [],
		initialConversation: null,
	});
	const [areConversationsLoading, setAreConversationsLoading] = useState(true);

	/**
	 * Loads all conversations for this RFx
	 */
	useEffect(() => {
		if (!rfxId || currentTab !== RFX_TABS.COMMS) {
			return;
		}

		setAreConversationsLoading(true);

		fetchConversationsForClientRfx(rfxId)
			.then((conversations) => {
				const initialConversation =
					conversations.find((c) => c.id.toString() === contextId) || null;
				setConversations({ conversations, initialConversation });
			})
			.finally(() => setAreConversationsLoading(false));
	}, [rfxId, contextId, currentTab]);

	const clarificationSuppliers = useMemo(() => {
		if (!rfxData?.supplierInvited?.submitted) {
			return undefined;
		}
		return rfxData.supplierInvited.submitted.map((s) => ({
			id: s.id,
			name: s.name ?? s.company?.name,
			logo: null,
		}));
	}, [rfxData?.supplierInvited?.submitted]);

	const onConversationSelected = (conversation) => {
		const path = clientPaths?.viewRfx(rfxData, RFX_TABS.COMMS, conversation.id);
		window.history.replaceState({}, "", path);
	};

	const onConversationUpdated = (updatedConversation) => {
		const updatedConversations = conversations.map((conversation) => conversation.id === updatedConversation.id ? updatedConversation : conversation);

		setConversations({
			conversations: updatedConversations,
			initialConversation: initialConversation,
		});
	};

	const unCheckedList = useMemo(() => {
		const listAllSuppliers = rfxData?.supplierList?.suppliers;
		const listAllInvitedSuppliers = rfxData?.supplierInvited?.all?.filter(
			(invited) => (invited?.company ? true : false)
		);

		let listAllInvitedSuppliersId = [];
		let tempAllUnInvitedSuppliers = [];
		if (rfxData) {
			if (listAllInvitedSuppliers?.length > 0) {
				for (let a = 0; a < listAllInvitedSuppliers.length; a++) {
					listAllInvitedSuppliersId.push(
						listAllInvitedSuppliers[a]?.company?.id
					);
				}
			}
			if (
				listAllSuppliers &&
				listAllSuppliers.length > listAllInvitedSuppliersId.length
			) {
				for (let a = 0; a < listAllSuppliers.length; a++) {
					if (!listAllInvitedSuppliersId.includes(listAllSuppliers[a].id)) {
						tempAllUnInvitedSuppliers.push({
							value: listAllSuppliers[a]?.id,
							label: listAllSuppliers[a]?.name,
						});
					}
				}
			}
		}
		return tempAllUnInvitedSuppliers;
	}, [rfxData]);

	const inviteSupplierHandler = (allInvited) => {
		const newData = { ...rfxData, supplierInvited: allInvited };
		updateLocalRfxData(newData);
	};

	const refreshData = () => {
		try {
			reloadRfxData();
		} catch {
			setIsNotFound(true);
		}
	};

	// useEffect(() => {
	// 	rfqContentRef?.current?.scrollIntoView();
	// }, [currentTab]);

	const handleCurrentTabChange = (newTab) => {
		navigate(clientPaths.viewRfx(rfxData, newTab));
	};

	// useEffect(() => {
	// 	const scrollToRef = (ref) => {
	// 		ref.current.scrollIntoView({
	// 			behavior: "smooth",
	// 			block: "start",
	// 		});
	// 	};

	// 	if (!isMobile) {
	// 		return;
	// 	}

	// 	switch (currentTab) {
	// 		case RFX_TABS.ADDENDUMS:
	// 		case RFX_TABS.COMMS:
	// 			scrollToRef(addendumRef);
	// 			break;
	// 		default:
	// 			scrollToRef(supplierRef);
	// 	}
	// }, [currentTab, isMobile]);

	const typeGuestName = (gdata) => {
		if (gdata?.guestname !== undefined && gdata?.guestname !== "") {
			setGuestName(gdata?.guestname);
		}
		if (gdata?.email !== undefined && gdata?.email !== "") {
			setGuestEmail(gdata?.email);
		}
	};

	const [typeModal, setTypeModal] = useState({
		type: "",
		companyId: null,
		projectId: null,
	});

	const { isOpen, onOpen, onClose } = useDisclosure();

	const feedBackModalType = ["award", "archive", "decline", "pause", "cancel"];

	const isSubmitted = (status) => {
		return rfxSubmission.constants.HAS_SUBMITTED.includes(status.toUpperCase());
	};

	const handleEditRFQ = () => {
		isSubmissionDateExtendable
			? navigate(clientPaths.viewRfxDetails(rfxId))
			: RfxIsAwarded || isRFQReadyForAward(rfxData?.status)
				? navigate(clientPaths.viewRfxDetails(rfxId))
				: navigate(clientPaths.editRfx(rfxId));
	};

	const handleAwardDeclineOnClose = () => {
		setGuestName(null);
		setGuestEmail(null);
		onClose();
	};

	const navigateCompareSelected = async (page) => {
		const companyId = rfxData?.companyId;
		const supplierListId = rfxData?.supplierList?.id;
		const supplierListName = rfxData?.supplierList?.name;
		const checkedIds = checkedRow.map((company) => company?.company?.id);

		if (page === "shortlist") {
			let payload = {
				companyId: companyId,
				supplierIds: checkedIds,
			};
			let response = await apiV2.post(
				`/supplier/${supplierListId}/shortlist`,
				payload,
				{
					responseType: "blob",
				}
			);
			if (response) {
				const file = new Blob([response.data], {
					type: "application/pdf",
				});

				const fileURL = URL.createObjectURL(file);
				window.open(fileURL);
			}
		} else {
			await dispatch(removeAllToListDirect());
			setTimeout(() => {
				window.open(
					`../../../search/direct/compare?supplier=${supplierListId}&supplierlist=${supplierListName}&directcompare=true&suppliers=${checkedIds.join(",")}`
				);
			}, 100);
		}
	};

	const onSubmissionDateExtension = (rfxDataWithExtension) => {
		updateLocalRfxData(rfxDataWithExtension);
	};

	if (isNotFound) {
		return <NotFound />;
	}

	const onBackButtonClick = () => {
		// Return to the tab this RFx is actually listed under, not the generic
		// RFx landing (which always resolved to Current). Nothing carries the
		// originating tab into this page — the detail URL has no slot for it —
		// so the RFx's own status re-derives it: an evaluating RFx goes back to
		// Evaluation, a draft to Drafts, an archived one to Archived.
		navigate(
			clientPaths.rfx.tendersQuotesTab(getRfxListTabForStatus(rfxData?.status))
		);
	};

	const showLoading = !hasRfxLoaded || isLoading;
	const totalAddendumAndComms = {
		totalAddendum: rfxTabTotals.addendums,
		totalComms: rfxTabTotals.comms,
		totalClarifications: rfxTabTotals.clarifications || 0,
	};

	const renderContent = () => {
		if (showLoading) {
			return <LoadingSpinner />;
		}

		switch (currentTab) {
			case RFX_TABS.COMMS:
				return (
					<CommunicationsPanel
						canCreateConversation={false}
						conversations={conversations}
						isLoading={areConversationsLoading}
						initialConversation={initialConversation}
						onConversationSelected={onConversationSelected}
						onConversationUpdated={onConversationUpdated}
						noConversationsMessage="There are currently no communications from any Suppliers"
					/>
				);
			case RFX_TABS.ADDENDUMS:
				return (
					<ClientAddendumContainer
						ref={addendumRef}
						rfqDetail={rfxData}
						addendumData={addendumData}
						setAddendumData={setAddendumData}
						isCommunication={currentTab === RFX_TABS.COMMS}
						setTotalAddendumAndComms={setRfxTabTotals}
						totalAddendumAndComms={totalAddendumAndComms}
					/>
				);
			case RFX_TABS.CLARIFICATIONS:
				return (
					<ClarificationsRegister
						rfxId={rfxId}
						isClientView={true}
						openSupplierOnClick
						allSuppliers={clarificationSuppliers}
					/>
				);
			case RFX_TABS.EVALUATION:
				return (
					<EvaluationTabDisplay
						rfxData={rfxData}
						isClientView={true}
					/>
				);
			default:
				return (
					<div id="rfq-detail-content-table">
						<RFQListSubmission
							ref={supplierRef}
							onOpen={onOpen}
							setTypeModal={setTypeModal}
							checkedRow={checkedRow}
							setCheckedRow={setCheckedRow}
							rfqDetail={rfxData}
							isLoading={showLoading}
							currentTab={currentTab}
							typeGuestName={typeGuestName}
							isSubmitted={isSubmitted}
							navigateCompareSelected={navigateCompareSelected}
							setGuestEmail={setGuestEmail}
							setGuestName={setGuestName}
							isResponseRestricted={isResponseRestricted}
						/>
					</div>
				);
		}
	};

	return (
		<div
			id="rfq-detail-container"
			className="overflow-hidden"
		>
			<div id="rfq-detail-wrapper">
				<div id="rfq-detail-header">
					<TabHeader
						icon={<BackButton handleBackButtonClick={onBackButtonClick} />}
					>
						<Flex
							gap={4}
							align={"center"}
						>
							{isRFQReadyForAward(rfxData?.status) &&
								!rfxData?.evaluationStarted && (
								<ReadOnlyGuard>
									<UtilityButton
										className="!h-12 !max-h-12"
										text={"Award Supplier"}
										onClick={() => {
											setTypeModal({ type: "award", companyId: "" });
											onOpen();
										}}
									/>
								</ReadOnlyGuard>
							)}
							{isSubmissionDateExtendable && !hasDeadlinePassed && (
								<ReadOnlyGuard>
									<Button
										btntype="secondary"
										onClick={() => setIsExtendDateModelOpen(true)}
									>
										Extend Deadline
									</Button>
								</ReadOnlyGuard>
							)}
							{(isRFQEditable(rfxData?.status) ||
								isSubmissionDateExtendable ||
								RfxIsAwarded ||
								isRFQReadyForAward(rfxData?.status)) && (
									<ReadOnlyGuard>
										<Button
											btntype="secondary"
											onClick={handleEditRFQ}
										>
											{isSubmissionDateExtendable
												? "View RFx Details"
												: RfxIsAwarded || isRFQReadyForAward(rfxData?.status)
													? "View RFx Details"
													: "Edit RFx"}
										</Button>
									</ReadOnlyGuard>
								)}
							{isSubmissionDateExtendable && !hasDeadlinePassed && (
								<ReadOnlyGuard>
									<InviteSupplierButton
										setTypeModal={setTypeModal}
										onOpen={onOpen}
									/>
								</ReadOnlyGuard>
							)}
						</Flex>
					</TabHeader>
				</div>
				<div
					id="rfq-detail-content"
					className="px-1"
				>
					<HeaderCard
						rfqDetail={rfxData}
						isLoading={showLoading}
					/>
					<div
						ref={rfqContentRef}
						className="my-9"
					>
						<Flex
							direction={{ base: "column", md: "row" }} // stack on mobile, row on desktop
							w="full"
							gap={2}
							align={{ base: "stretch", md: "center" }}
						>
							<RFQDetailButtonTab
								currentTab={currentTab}
								setCurrentTab={handleCurrentTabChange}
								rfqDetail={rfxData}
								totalAddendumAndComms={totalAddendumAndComms}
							/>
							{!isResponseRestricted && (
								<PdfDownloadButton
									className="mr-8 w-full self-start md:w-auto md:self-auto"
									rfxId={rfxData?.id}
								/>
							)}
						</Flex>
					</div>
					{renderContent()}
				</div>
			</div>

			{(typeModal === "add_supplier" || typeModal === "invite_guest") && (
				<InviteSupplierDrawer
					rfqName={rfxData.displayName}
					onClose={onClose}
					isOpen={isOpen}
					rfqId={rfxId}
					inviteSupplierHandler={inviteSupplierHandler}
					rfqDetail={rfxData}
					unCheckedList={unCheckedList}
					onInvitationSent={setInvitationSentEmails}
					isGuest={typeModal === "invite_guest"}
				/>
			)}
			<InvitationSentModal
				isOpen={Boolean(invitationSentEmails?.length)}
				onClose={() => setInvitationSentEmails(null)}
				recipientEmails={invitationSentEmails ?? []}
			/>
			{feedBackModalType.includes(typeModal.type) && (
				<AwardDeclineSupplierDrawer
					isOpen={isOpen}
					guestName={guestName}
					guestEmail={guestEmail}
					onClose={handleAwardDeclineOnClose}
					rfq={rfxData}
					typeModal={typeModal}
					setLoadRFQData={setIsLoading}
					refreshData={refreshData}
				/>
			)}
			{isSubmissionDateExtendable && (
				<ExtendSubmissionDeadlineModal
					isModalOpen={isExtendDateModelOpen}
					rfqDetail={rfxData}
					onSubmissionDateExtension={onSubmissionDateExtension}
					onClose={() => setIsExtendDateModelOpen(false)}
				/>
			)}
		</div>
	);
};

export default ProcurerRFx;